import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { OnHoldReason } from 'enums/OnHoldReason';
import { environment } from 'environments/environment';
import { SelectListItemModel } from 'models/SelectList';
import { InvoiceObjectionSearchRequestModel } from 'models/generated/Accounting.Api.Models.InvoiceObjectionSearchRequest';
import { OnHoldRequestModel } from 'models/generated/Accounting.Api.Models.OnHoldRequest';
import { CustomerLedgerEntryModel } from 'models/generated/BusinessCentral.Integration.Models.CustomerLedgerEntry';
import { IssuedReminderModel } from 'models/generated/BusinessCentral.Integration.Models.IssuedReminder';
import { InvoiceObjectionModel } from 'models/generated/Entities.Accounting.InvoiceObjection';
import { firstValueFrom } from 'rxjs';
import { Base64File, FileStorageService } from './file-storage.service';
import { BookedEntryModel } from 'models/generated/Economic.Integration.Clients.EconomicBookedEntriesClient.BookedEntry';
import { BcDocumentType } from 'enums/BcDocumentType';
import { BillingDocumentLineModel } from 'models/generated/Accounting.IntegrationAbstractions.BillingDocumentLine';
import { LedgerHeaderModel } from 'models/generated/Entities.Accounting.LedgerHeader';

@Injectable()
export class InvoicingService {
	private apiUrl = `${environment.gatewayUrl}/api/invoicing`;

	constructor(private readonly http: HttpClient, private readonly fileStorageService: FileStorageService) {
	}

	// Select List
	private getSortedDimensionsSelectList(dimensionValues: DimensionValue[], dimension: string): SelectListItemModel[] {
		if (dimensionValues.length === 0) throw new Error(`Missing ${dimension} dimensions`);
		return dimensionValues.sort((d1, d2) => d1.code.localeCompare(d2.code)).filter(d => !d.blocked).map(dv => new SelectListItemModel(dv.code, dv.name != '' ? dv.name : dv.code));
	}

	getProductDimensionsSelectList(): Promise<SelectListItemModel[]> {
		return this.getProductDimensions().then(dimensionValues => this.getSortedDimensionsSelectList(dimensionValues, "product"));
	}

	getDepartmentDimensionsSelectList(): Promise<SelectListItemModel[]> {
		return this.getDepartmentDimensions().then(dimensionValues => this.getSortedDimensionsSelectList(dimensionValues, "department"));
	}

	getCompanyDimensionsSelectList(): Promise<SelectListItemModel[]> {
		return this.getCompanyDimensions().then(dimensionValues => this.getSortedDimensionsSelectList(dimensionValues, "company"));
	}

	getBcItemsSelectList(): Promise<SelectListItemModel[]> {
		return this.getBcItems().then(items => {
			if (items.length === 0) throw new Error('Missing business central items');
			return items.map(item => new SelectListItemModel(item.no, item.description));
		});
	};

	// Dimensions list
	private getProductDimensions(): Promise<DimensionValue[]> {
		return firstValueFrom(this.http.get<DimensionValue[]>(`${this.apiUrl}/dimension-values/PRODUKT`));
	}

	private getDepartmentDimensions(): Promise<DimensionValue[]> {
		return firstValueFrom(this.http.get<DimensionValue[]>(`${this.apiUrl}/dimension-values/AFDELING`));
	}

	private getCompanyDimensions(): Promise<DimensionValue[]> {
		return firstValueFrom(this.http.get<DimensionValue[]>(`${this.apiUrl}/dimension-values/VIRKSOMHEDSTYPE`));
	}

	private getBcItems(): Promise<BusinessCentralItem[]> {
		return firstValueFrom(this.http.get<BusinessCentralItem[]>(`${this.apiUrl}/items`));
	}

	exportLedgerPosts(ledgerPostInfos: { postId: string, description: string }[]): Promise<{ referenceId: string, total: number }> {
		return firstValueFrom(this.http.post<{ referenceId: string, total: number }>(`${this.apiUrl}/register-ledger-posts`, ledgerPostInfos));
	}

	getBusinessCentralLedgerEntriesByCompanyId(companyId: string, brandId: string): Promise<CustomerLedgerEntryModel[]> {
		return firstValueFrom(this.http.get<CustomerLedgerEntryModel[]>(`${this.apiUrl}/business-central-ledger-entries-by-company/${companyId}`, { params: { brandId } }));
	}

	getEconomicLedgerEntriesByCompanyId(companyId: string, brandId: string): Promise<BookedEntryModel[]> {
		return firstValueFrom(this.http.get<BookedEntryModel[]>(`${this.apiUrl}/e-conomic-ledger-entries-by-company/${companyId}`, { params: { brandId } }));
	}
		
	getCustomerLedgerEntriesByDocumentNos(documentNos: string[], brandId: string): Promise<CustomerLedgerEntryModel[]> {
		return firstValueFrom(this.http.post<CustomerLedgerEntryModel[]>(`${this.apiUrl}/customer-ledger-entries-by-documentnos`, documentNos, { params: { brandId } }));
	}

	getInvoiceObjections(documents: { documentNo?: string, brandId?: string; }[]): Promise<InvoiceObjectionModel[]> {
		if (documents.length == 0) return new Promise(() => new Array<InvoiceObjectionModel>());
		return firstValueFrom(this.http.post<InvoiceObjectionModel[]>(`${this.apiUrl}/get-invoice-objections`, documents));
	}
	
	async getIssuedReminders(customerNos: string[]): Promise<IssuedReminderModel[]> {
		if (customerNos.length == 0) return new Array<IssuedReminderModel>();
		return await firstValueFrom(this.http.post<IssuedReminderModel[]>(`${this.apiUrl}/reminders`, customerNos));
	}
	
	createOrUpdateInvoiceObjection(invoiceObjection: InvoiceObjectionModel, setInvoiceOnHold: boolean): Promise<InvoiceObjectionModel> {
		return firstValueFrom(this.http.post<InvoiceObjectionModel>(`${this.apiUrl}/invoice-objections?setInvoiceOnHold=${setInvoiceOnHold}`, invoiceObjection));
	}

	editInvoiceDueDate(ledgerHeaderId: string, dueDate: Date) {
		return firstValueFrom(this.http.put<void>(`${this.apiUrl}/due-date?ledgerHeaderId=${ledgerHeaderId}`, dueDate));
	}

	searchInvoiceObjections(searchRequest: InvoiceObjectionSearchRequestModel): Promise<InvoiceObjectionModel[]> {
		return firstValueFrom(this.http.post<InvoiceObjectionModel[]>(`${this.apiUrl}/search-invoice-objections`, searchRequest));
	}

	getCreditMemoIdsByCompanyId(companyId: string, brandId: string): Promise<Record<string, string>> {
		return firstValueFrom(this.http.get<Record<string, string>>(`${this.apiUrl}/creditmemo-ids-by-company/${companyId}`, { params: { brandId } }));
	}

	getPostedDocumentLines(documentNo: string, type: BcDocumentType, brandId: string): Promise<BillingDocumentLineModel[]> {
		return firstValueFrom(this.http.get<BillingDocumentLineModel[]>(`${this.apiUrl}/posted-document-lines/${brandId}/${documentNo}`, { params: { type } }));
	}

	getSalesInvoiceIdsByCompanyId(companyId: string, brandId: string): Promise<Record<string, string>> {
		return firstValueFrom(this.http.get<Record<string, string>>(`${this.apiUrl}/salesinvoice-ids-by-company/${companyId}`, { params: { brandId }}));
	}

	sendSalesInvoice(documentId: string, brandId: string) {
		return firstValueFrom(this.http.post<void>(`${this.apiUrl}/send-salesinvoice-by-id/${documentId}`, null, { params: { brandId }}));
	}
	sendCreditMemo(documentId: string, brandId: string) {
		return firstValueFrom(this.http.post<void>(`${this.apiUrl}/send-creditmemo-by-id/${documentId}`, null, { params: { brandId } }));
	}
	downloadSalesInvoice(documentId: string, filename: string, brandId: string) {
		this.http.get<Base64File>(`${this.apiUrl}/download-salesinvoice-by-id/${documentId}`, { params: { brandId }}).subscribe(response => {
			this.fileStorageService.downloadFileByBase64(response, filename || 'faktura.pdf');
		});
	}
	downloadCreditMemo(documentId: string, filename: string, brandId: string) {
		this.http.get<Base64File>(`${this.apiUrl}/download-creditmemo-by-id/${documentId}`, { params: { brandId } }).subscribe(response => {
			this.fileStorageService.downloadFileByBase64(response, filename || 'kreditnota.pdf');
		});
	}

	updateOnHold(ledgerHeaderId: string, companyId: string, onHoldReason?: OnHoldReason) {
		const request = new OnHoldRequestModel();
		request.ledgerHeaderId = ledgerHeaderId;
		request.onHoldReason = onHoldReason;
		request.companyId = companyId;
		return firstValueFrom(this.http.post(`${this.apiUrl}/on-hold`, request));
	}

	getInvoicesWithOverriddenDueDates(brandId: string) {
		return firstValueFrom(this.http.get<CustomerInvoiceInformation[]>(`${this.apiUrl}/invoices-with-overridden-due-dates`, { params: { brandId } }));
	}
}

export interface CustomerInvoiceInformation {
	companyId: string;
	customerNumber: string;
	ledgerHeaders: LedgerHeaderModel[];
}

export class BusinessCentralItem {
	no!: string;
	description!: string;
}
export class DimensionValue {
	dimensionCode!: string;
	dimensionName!: string;
	code!: string;
	name!: string;
	blocked!: boolean;
}
